import { Button } from "@/components/ui/button";
import {
  Phone,
  Clock,
  X,
  Tag,
  ChevronDown,
  Download,
  Calendar,
  Languages,
  ChevronUp,
} from "lucide-react";
import { PatientResponse } from "@/lib/types";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState } from "react";
import AddTagsModal from "./AddTagsModal";
import BulkLanguageUpdateModal from "./BulkLanguageUpdateModal";
import { downloadPatientsCSV, downloadCarePlansZip } from "@/lib/utils";
import { useOrganizationsStore } from "@/stores/organizationsStore";
import { api } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

interface PatientSelectionSidebarProps {
  selectedPatients: PatientResponse[];
  onRemovePatient: (patientId: string) => void;
  onCallPatients: () => void;
  onScheduleCall: () => void;
  onScheduleCheckinCall: () => void;
  onScheduleBatchCall: () => void;
  defaultPhoneNumberId: string | null;
  onClearAllPatients: () => void;
}

const PatientSelectionSidebar = ({
  selectedPatients,
  onRemovePatient,
  onCallPatients,
  onScheduleCall,
  onScheduleCheckinCall,
  onScheduleBatchCall,
  defaultPhoneNumberId,
  onClearAllPatients,
}: PatientSelectionSidebarProps) => {
  const [isAddTagsModalOpen, setIsAddTagsModalOpen] = useState(false);
  const [isBulkLanguageModalOpen, setIsBulkLanguageModalOpen] = useState(false);
  // Phone-only: the selection list is collapsed into a bottom sheet header until expanded.
  const [isListExpanded, setIsListExpanded] = useState(false);
  const { selectedOrganizationId } = useOrganizationsStore();
  const { toast } = useToast();

  const handleDownloadCarePlans = async () => {
    if (!selectedOrganizationId) {
      toast({
        title: "Error",
        description: "No organization selected",
        variant: "destructive",
      });
      return;
    }

    if (selectedPatients.length === 0) {
      toast({
        title: "Error",
        description: "No patients selected",
        variant: "destructive",
      });
      return;
    }

    try {
      const patientIds = selectedPatients.map((patient) => patient.id);
      const blob = await api.downloadMultipleCarePlanPDFs(
        patientIds,
        selectedOrganizationId
      );

      downloadCarePlansZip(blob);

      toast({
        title: "Success",
        description: "Care plans downloaded successfully",
      });
    } catch (error) {
      console.error("Failed to download care plans:", error);
      toast({
        title: "Error",
        description: "Failed to download care plans",
        variant: "destructive",
      });
    }
  };

  if (selectedPatients.length === 0) return null;

  return (
    <div className="h-auto md:h-full w-full flex flex-col">
      <Card className="h-auto md:h-full flex flex-col rounded-b-none border-b-0 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] md:rounded-b-xl md:border-b md:shadow-none">
        <CardHeader className="pb-2 pt-3 border-b bg-muted/30 flex-shrink-0">
          <div className="flex justify-between items-center">
            <button
              type="button"
              onClick={() => setIsListExpanded((v) => !v)}
              className="flex items-center gap-1 font-light text-sm md:pointer-events-none"
              aria-expanded={isListExpanded}
            >
              Selected Patients ({selectedPatients.length})
              {isListExpanded ? (
                <ChevronUp className="h-4 w-4 md:hidden" />
              ) : (
                <ChevronDown className="h-4 w-4 md:hidden" />
              )}
            </button>
            <Button
              variant="outline"
              size="sm"
              onClick={onClearAllPatients}
              className="h-7 px-2 text-xs"
            >
              Clear
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-3 space-y-2 flex-shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="w-full justify-between">
                Actions
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-80">
              <DropdownMenuItem
                onClick={onCallPatients}
                disabled={!defaultPhoneNumberId}
              >
                <Phone className="h-4 w-4 mr-2" />
                Call Patients
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onScheduleCall}
                disabled={!defaultPhoneNumberId}
              >
                <Clock className="h-4 w-4 mr-2" />
                Schedule APCM Consent Calls
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onScheduleCheckinCall}
                disabled={!defaultPhoneNumberId}
              >
                <Clock className="h-4 w-4 mr-2" />
                Schedule APCM Check In Calls
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onScheduleBatchCall}
                disabled={!defaultPhoneNumberId}
              >
                <Calendar className="h-4 w-4 mr-2" />
                Schedule Calls in Batches
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setIsAddTagsModalOpen(true)}>
                <Tag className="h-4 w-4 mr-2" />
                Add Tags
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setIsBulkLanguageModalOpen(true)}
              >
                <Languages className="h-4 w-4 mr-2" />
                Update Language
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => downloadPatientsCSV(selectedPatients)}
              >
                <Download className="h-4 w-4 mr-2" />
                Download CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDownloadCarePlans}>
                <Download className="h-4 w-4 mr-2" />
                Download Care Plans
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardContent>

        <div
          className={`px-2 pb-2 min-h-0 max-h-[40dvh] overflow-y-auto md:max-h-none md:flex-1 md:overflow-hidden ${
            isListExpanded ? "block" : "hidden"
          } md:block`}
        >
          <ScrollArea className="h-auto md:h-full w-full pr-2">
            <div className="space-y-1">
              {selectedPatients.map((patient) => (
                <div
                  key={patient.id}
                  className="flex items-center justify-between p-2 rounded hover:bg-muted/50 group"
                >
                  <span className="truncate text-sm">
                    {patient.first_name} {patient.last_name}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onRemovePatient(patient.id)}
                    className="h-6 w-6 transition-opacity"
                  >
                    <X className="h-4 w-4 text-gray-200 group-hover:text-muted-foreground" />
                  </Button>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      </Card>

      <AddTagsModal
        isOpen={isAddTagsModalOpen}
        onClose={() => setIsAddTagsModalOpen(false)}
        patientIds={selectedPatients.map((patient) => patient.id)}
      />

      <BulkLanguageUpdateModal
        isOpen={isBulkLanguageModalOpen}
        onClose={() => setIsBulkLanguageModalOpen(false)}
        patientIds={selectedPatients.map((patient) => patient.id)}
      />
    </div>
  );
};

export default PatientSelectionSidebar;
